"use client";

import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  WebAuthnAbortService,
  type AuthenticationResponseJSON,
} from "@simplewebauthn/browser";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useActionState, useEffect, useRef, useState, useTransition, type ReactNode } from "react";

import {
  passkeyLoginAction,
  passkeyOptionsAction,
  type LoginState,
  type LoginStep,
} from "@/app/login/actions";
import { PasswordInput } from "@/components/password-input";

type LoginAction = (prev: LoginState, formData: FormData) => Promise<LoginState>;

type Stage = LoginStep | "passkey";

const INPUT =
  "rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20";
const PRIMARY_BUTTON =
  "mt-1 inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60";
const LINK_BUTTON =
  "text-sm font-medium text-primary underline-offset-4 hover:underline focus:outline-none focus-visible:underline disabled:opacity-60";
const QUIET_BUTTON = "text-sm text-muted-foreground hover:text-foreground disabled:opacity-60";

// The email-first sign-in:
//
//   email     one field (autocomplete="username webauthn"), so the browser can
//             offer this site's passkeys right in the field (conditional
//             mediation) and a passkey user is signed in immediately.
//   passkey   after "Continue" we start a passkey sign-in by default. The
//             options are the same discoverable-credential options for every
//             email, so nothing here reveals whether the email has an account.
//   password  always one click away ("Use password instead").
//   code      authenticator or recovery code, only after a correct password on
//             an account that has an authenticator. The server decides this.
//
// A session only exists once a server action says the last step passed. Clients
// and staff use this same form; the server works out which kind of account the
// email or passkey belongs to, and nothing on the page says which it was.
// "Forgot password?" (on the password step) is shown only when this app can
// send the reset email itself.
export function LoginFlow({
  action,
  callbackUrl,
  canResetPassword = false,
}: {
  action: LoginAction;
  callbackUrl: string;
  canResetPassword?: boolean;
}) {
  const t = useTranslations("auth");
  const [state, formAction, pending] = useActionState(action, { step: "email" });
  const [stage, setStage] = useState<Stage>("email");
  const [message, setMessage] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [passkeyPending, startPasskeyTransition] = useTransition();
  const codeRef = useRef<HTMLInputElement>(null);

  // Each server answer (it carries a fresh nonce) decides the next stage.
  const [seenNonce, setSeenNonce] = useState(state.nonce);
  if (state.nonce !== seenNonce) {
    setSeenNonce(state.nonce);
    setStage(state.step);
    setMessage(state.error ?? null);
  }

  function goTo(next: Stage) {
    setMessage(null);
    setStage(next);
  }

  function finishPasskey(response: AuthenticationResponseJSON) {
    startPasskeyTransition(async () => {
      // Redirects on success; only a failure comes back.
      const result = await passkeyLoginAction(JSON.stringify(response), callbackUrl);
      if (result?.error) {
        setMessage(result.error);
        setAttempt((n) => n + 1);
      }
    });
  }

  // Passkey autofill on the email field while it's shown. Cleanup aborts the
  // pending (invisible) ceremony when the stage changes.
  useEffect(() => {
    if (stage !== "email") return;
    let active = true;
    (async () => {
      if (!(await browserSupportsWebAuthnAutofill())) return;
      const optionsJSON = await passkeyOptionsAction();
      if (!optionsJSON || !active) return;
      try {
        const response = await startAuthentication({ optionsJSON, useBrowserAutofill: true });
        if (active) finishPasskey(response);
      } catch {
        // Aborted (stage change) or dismissed: nothing to show.
      }
    })();
    return () => {
      active = false;
      WebAuthnAbortService.cancelCeremony();
    };
    // finishPasskey only closes over stable setters and callbackUrl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, attempt]);

  // The explicit passkey prompt after "Continue" (and on "Try again").
  useEffect(() => {
    if (stage !== "passkey") return;
    let active = true;
    (async () => {
      const optionsJSON = browserSupportsWebAuthn() ? await passkeyOptionsAction() : null;
      if (!active) return;
      if (!optionsJSON) {
        // Passkeys unavailable here: fall straight through to the password.
        setStage("password");
        return;
      }
      try {
        const response = await startAuthentication({ optionsJSON });
        if (active) finishPasskey(response);
      } catch {
        if (active) setMessage(t("passkeyCancelled"));
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, attempt]);

  // Clear and refocus the code input after every answer on the code step.
  useEffect(() => {
    if (stage === "code" && codeRef.current) {
      codeRef.current.value = "";
      codeRef.current.focus();
    }
  }, [stage, state.nonce, useRecovery]);

  const alert = message && (
    <p
      role="alert"
      className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-400"
    >
      {message}
    </p>
  );

  const signingInAs = (
    <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
      <span className="min-w-0 truncate text-foreground" title={email}>
        {email}
      </span>
      <button type="button" className={LINK_BUTTON} onClick={() => goTo("email")}>
        {t("changeEmail")}
      </button>
    </div>
  );

  let body: ReactNode;

  if (stage === "code") {
    body = (
      <form action={formAction} className="flex flex-col gap-4" noValidate>
        {/* The submit buttons carry `intent` (Enter submits with the first
            one), so "Start over" can share this form. */}
        <input type="hidden" name="callbackUrl" value={callbackUrl} />
        <input type="hidden" name="method" value={useRecovery ? "recovery" : "totp"} />
        <p className="text-sm text-muted-foreground">
          {useRecovery ? t("recoveryPrompt") : t("codePrompt")}
        </p>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="code" className="text-sm font-medium text-foreground">
            {useRecovery ? t("recoveryLabel") : t("codeLabel")}
          </label>
          <input
            ref={codeRef}
            key={useRecovery ? "recovery" : "totp"}
            id="code"
            name="code"
            type="text"
            required
            autoFocus
            autoComplete="one-time-code"
            inputMode={useRecovery ? "text" : "numeric"}
            maxLength={useRecovery ? 16 : 7}
            spellCheck={false}
            aria-invalid={message ? true : undefined}
            className={`${INPUT} font-mono tracking-widest`}
          />
        </div>
        {alert}
        <button type="submit" name="intent" value="code" disabled={pending} className={PRIMARY_BUTTON}>
          {pending ? t("verifying") : t("verify")}
        </button>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button type="button" className={LINK_BUTTON} onClick={() => setUseRecovery((v) => !v)}>
            {useRecovery ? t("useAuthenticatorInstead") : t("useRecoveryInstead")}
          </button>
          <button
            type="submit"
            name="intent"
            value="restart"
            formNoValidate
            disabled={pending}
            className={QUIET_BUTTON}
          >
            {t("startOver")}
          </button>
        </div>
      </form>
    );
  } else if (stage === "password") {
    body = (
      <form action={formAction} className="flex flex-col gap-4" noValidate>
        <input type="hidden" name="intent" value="password" />
        <input type="hidden" name="callbackUrl" value={callbackUrl} />
        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-sm font-medium text-foreground">
            {t("emailLabel")}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            required
            autoFocus={!email}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={INPUT}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="password" className="text-sm font-medium text-foreground">
            {t("passwordLabel")}
          </label>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            required
            autoFocus={!!email}
            aria-invalid={message ? true : undefined}
            className={INPUT}
          />
        </div>
        {alert}
        <button type="submit" disabled={pending} className={PRIMARY_BUTTON}>
          {pending ? t("signingIn") : t("signIn")}
        </button>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button type="button" className={LINK_BUTTON} onClick={() => goTo("passkey")}>
            {t("usePasskeyInstead")}
          </button>
          {canResetPassword && (
            <Link href="/forgot-password" className={LINK_BUTTON}>
              {t("forgotPassword")}
            </Link>
          )}
        </div>
      </form>
    );
  } else if (stage === "passkey") {
    body = (
      <div className="flex flex-col gap-4">
        {signingInAs}
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {passkeyPending ? t("signingIn") : t("passkeyWaiting")}
        </p>
        {alert}
        {message && (
          <button
            type="button"
            disabled={passkeyPending}
            className={PRIMARY_BUTTON}
            onClick={() => {
              setMessage(null);
              setAttempt((n) => n + 1);
            }}
          >
            {t("tryAgain")}
          </button>
        )}
        <button type="button" className={`${LINK_BUTTON} self-start`} onClick={() => goTo("password")}>
          {t("usePasswordInstead")}
        </button>
      </div>
    );
  } else {
    body = (
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim()) return setMessage(t("emailRequired"));
          goTo("passkey");
        }}
      >
        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-sm font-medium text-foreground">
            {t("emailLabel")}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            // "webauthn" lets the browser offer passkeys in the field's autofill.
            autoComplete="username webauthn"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={message ? true : undefined}
            className={INPUT}
          />
        </div>
        {alert}
        <button type="submit" disabled={passkeyPending} className={PRIMARY_BUTTON}>
          {passkeyPending ? t("signingIn") : t("continue")}
        </button>
        <button type="button" className={`${LINK_BUTTON} self-start`} onClick={() => goTo("password")}>
          {t("usePasswordInstead")}
        </button>
      </form>
    );
  }

  return body;
}
